import { connect } from "react-redux";
import ManageGroupMembersModal, {
  ManageGroupMembersModalProps as _ManageGroupMembersModalProps,
} from "./manage-group-members-modal-container";
import { AppState } from "../../../store/AppState";
import {
  PickReduxDispatchProps,
  PickReduxStateProps,
} from "../../../util/typeUtil";
import OrganizationUtils from "../../../utils/organization";
import {
  addUsersToOrganizationGroup,
  clearError,
  getOrganizationGroup,
  listOrganizationRolesWithUsers,
  listOrganizationUsers,
  listUsersInOrganizationGroup,
  removeUsersFromOrganizationGroup,
} from "../../../actions";
import { ManageGroupMembersModalDataProps } from "./manage-group-members-modal-view";
import InProgressUtils from "../../../utils/in-progress";
import { ActionSender } from "../../../model/ActionSender";
import AuthenticationUtils from "../../../utils/authentication";
import { addSenderArgument } from "../../../actions/actionHelpers";

export type ManageGroupMembersModalProps = Omit<
  _ManageGroupMembersModalProps,
  keyof ManageGroupMembersModalDataProps
>;

// Input props provided to the wrapped component by this connect component, using mapStateToProps
type ReduxStateProps = PickReduxStateProps<
  _ManageGroupMembersModalProps,
  ManageGroupMembersModalProps
>;
// Dispatch props provided to the wrapped component by this connect component
type ReduxDispatchProps = PickReduxDispatchProps<
  _ManageGroupMembersModalProps,
  ManageGroupMembersModalProps
>;

const sender: ActionSender = { key: "manage-group-members" };

function mapStateToProps(
  state: AppState,
  ownProps: ManageGroupMembersModalProps
): ReduxStateProps {
  const orgAdmins = OrganizationUtils.selectOrganizationAdminIds(state, false);
  let users = OrganizationUtils.selectOrganizationUsers(state);
  if (users) {
    users = users.map((usr) => {
      const u = !!usr ? { ...usr } : usr;
      (u as any).isOrgAdmin = orgAdmins
        ? orgAdmins.indexOf(u.id as string) !== -1
        : false;
      return u;
    });
  }
  return {
    adminsLoaded: !!orgAdmins,
    users,
    employeesGroupId:
      OrganizationUtils.selectOrganizationEmployeesGroupId(state),
    groupMembers: OrganizationUtils.selectOrganizationGroupUserIds(
      ownProps.groupId as string,
      state
    ),
    group: OrganizationUtils.selectOrganizationGroup(
      ownProps.groupId as string,
      state
    ),
    actorId: AuthenticationUtils.selectAuthenticationUserId(state) as string,
    isReady: InProgressUtils.selectNotInProgress(sender.key, state),
  };
}

function areStatesEqual(next: AppState, prev: AppState): boolean {
  return (
    AuthenticationUtils.compareAuthenticationUserIdStates(next, prev) &&
    InProgressUtils.compareInProgressStates(next, prev) &&
    OrganizationUtils.compareOrganizationRoleUserIdsStates(next, prev) &&
    OrganizationUtils.compareOrganizationRolesStates(next, prev) &&
    OrganizationUtils.compareOrganizationUsersStates(next, prev) &&
    OrganizationUtils.compareOrganizationGroupsStates(next, prev) &&
    OrganizationUtils.compareOrganizationGroupUserIdsStates(next, prev)
  );
}

const dispatchActions: ReduxDispatchProps = {
  onLoadUsers: addSenderArgument(sender, listOrganizationUsers),
  onLoadGroupMembers: addSenderArgument(sender, listUsersInOrganizationGroup),
  onAddMembers: addSenderArgument(sender, addUsersToOrganizationGroup),
  onRemoveMembers: addSenderArgument(sender, removeUsersFromOrganizationGroup),
  onLoadAdmins: addSenderArgument(sender, listOrganizationRolesWithUsers),
  onLoadGroup: addSenderArgument(sender, getOrganizationGroup),
  onClearError: clearError,
};

export default connect<
  ReduxStateProps,
  ReduxDispatchProps,
  ManageGroupMembersModalProps,
  AppState
>(mapStateToProps, dispatchActions, null, {
  areStatesEqual,
})(ManageGroupMembersModal);
